/**
 * DelinquencyTable Component
 * Shows delinquency distribution by week-range buckets
 */

import { Badge } from '@/components/ui/Badge';
import type { DelinquencyWeekBucket } from '@/types/collection.types';
import {
  formatCurrencyMXN,
  formatPercentage,
  WEEK_RANGE_LABEL,
  WEEK_RANGE_VARIANT,
} from '@/features/collection/types';

// ─── Week Bucket Distribution Table ─────────────────────────────────────────

interface BucketDistributionProps {
  rows: Record<string, DelinquencyWeekBucket>;
  totalOutstanding: number;
}

export function BucketDistributionTable({ rows, totalOutstanding }: BucketDistributionProps) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">
          Distribucion por Semanas de Mora
        </h3>
      </div>

      {Object.keys(rows ?? {}).length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          Sin datos de distribucion
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium">Rango</th>
                <th className="px-4 py-2 text-right font-medium">Cantidad</th>
                <th className="px-4 py-2 text-right font-medium">Monto</th>
                <th className="px-4 py-2 text-right font-medium">Porcentaje</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(rows ?? {}).map(([range, bucket]) => {
                const pct = totalOutstanding > 0
                  ? (bucket.total_outstanding_mxn / totalOutstanding) * 100
                  : 0;
                return (
                  <tr
                    key={range}
                    className="border-b border-border/50 transition-colors hover:bg-muted/30"
                  >
                    <td className="px-4 py-2">
                      <Badge
                        variant={(WEEK_RANGE_VARIANT[range] ?? 'default') as 'default' | 'success' | 'warning' | 'danger' | 'info'}
                      >
                        {WEEK_RANGE_LABEL[range] ?? range}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">
                      {bucket.count}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">
                      {formatCurrencyMXN(bucket.total_outstanding_mxn)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">
                      {formatPercentage(pct)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
